import { createFileRoute, Link } from "@tanstack/react-router";
import { Zap, ShieldCheck, MapPin, Coins, Building2, Hotel, Smartphone, Radar } from "lucide-react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/for-partners")({
  head: () => ({
    meta: [
      { title: "Transfer API for OTAs & Travel Platforms | Bacha Way Travel" },
      { name: "description", content: "Integrate Morocco's ground transport network into your platform via a simple REST API. Complete documentation, sandbox keys, dedicated technical support." },
      { property: "og:title", content: "Transfer API for OTAs & Travel Platforms" },
      { property: "og:description", content: "Connect your platform to Morocco's ground transport network. REST API, sandbox, dedicated support." },
      { property: "og:url", content: "https://bachawaytravel.com/for-partners" },
    ],
    links: [{ rel: "canonical", href: "https://bachawaytravel.com/for-partners" }],
  }),
  component: ForPartnersPage,
});

const endpoints = [
  { method: "POST", path: "/api/price-quote", key: "e1" },
  { method: "POST", path: "/api/booking", key: "e2" },
  { method: "GET", path: "/api/booking/:id", key: "e3" },
  { method: "PATCH", path: "/api/booking/:id", key: "e4" },
  { method: "WS", path: "/api/tracking/live", key: "e5" },
  { method: "GET", path: "/api/driver/status", key: "e6" },
] as const;

function ForPartnersPage() {
  const { t } = useTranslation();
  const features = [
    { icon: Zap, title: t("partners.f1t"), desc: t("partners.f1d") },
    { icon: ShieldCheck, title: t("partners.f2t"), desc: t("partners.f2d") },
    { icon: MapPin, title: t("partners.f3t"), desc: t("partners.f3d") },
    { icon: Coins, title: t("partners.f4t"), desc: t("partners.f4d") },
  ];
  const useCases = [
    { icon: Building2, title: t("partners.u1t"), desc: t("partners.u1d") },
    { icon: Hotel, title: t("partners.u2t"), desc: t("partners.u2d") },
    { icon: Smartphone, title: t("partners.u3t"), desc: t("partners.u3d") },
    { icon: Radar, title: t("partners.u4t"), desc: t("partners.u4d") },
  ];

  return (
    <>
      <section className="bg-hero-navy text-white">
        <div className="mx-auto max-w-7xl px-6 py-20 md:py-28 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 py-1.5 text-[10px] md:text-xs uppercase tracking-[0.25em] text-white/80">
            {t("partners.badge")}
          </div>
          <h1 className="mt-8 mx-auto max-w-3xl text-4xl md:text-6xl font-bold leading-[1.05]">
            {t("partners.title1")} <span className="text-gradient-sky">{t("partners.title2")}</span>
          </h1>
          <p className="mt-5 mx-auto max-w-2xl text-white/70">{t("partners.lead")}</p>
          <Link to="/contact" className="mt-8 inline-flex rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-primary">{t("partners.request")}</Link>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-xs uppercase tracking-[0.25em] text-primary font-semibold">{t("partners.featuresEyebrow")}</div>
          <h2 className="mt-3 text-3xl md:text-5xl font-bold">{t("partners.featuresTitle")}</h2>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((f) => (
              <div key={f.title} className="rounded-2xl border border-border bg-card p-7">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary"><f.icon className="h-6 w-6" /></div>
                <div className="mt-5 text-lg font-semibold">{f.title}</div>
                <p className="mt-2 text-sm text-muted-foreground">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-secondary/40">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-xs uppercase tracking-[0.25em] text-primary font-semibold">{t("partners.endpointsEyebrow")}</div>
          <h2 className="mt-3 text-3xl md:text-5xl font-bold">{t("partners.endpointsTitle")}</h2>
          <div className="mt-10 grid gap-3 max-w-3xl">
            {endpoints.map((e) => (
              <div key={e.path} className="flex items-center gap-4 rounded-xl border border-border bg-card p-4">
                <span className="rounded-md bg-primary px-2.5 py-1 text-[10px] font-bold tracking-wider text-primary-foreground w-14 text-center">{e.method}</span>
                <code className="font-mono text-sm text-foreground">{e.path}</code>
                <span className="ml-auto text-xs text-muted-foreground">{t(`partners.${e.key}`)}</span>
              </div>
            ))}
          </div>
          <p className="mt-6 text-sm text-muted-foreground">{t("partners.endpointsNote")}</p>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-xs uppercase tracking-[0.25em] text-primary font-semibold">{t("partners.useEyebrow")}</div>
          <h2 className="mt-3 text-3xl md:text-5xl font-bold">{t("partners.useTitle")}</h2>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {useCases.map((u) => (
              <div key={u.title} className="rounded-2xl border border-border bg-card p-7">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-primary-foreground"><u.icon className="h-6 w-6" /></div>
                <div className="mt-5 text-lg font-semibold">{u.title}</div>
                <p className="mt-2 text-sm text-muted-foreground">{u.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-secondary/40">
        <div className="mx-auto max-w-5xl px-6">
          <div className="rounded-3xl bg-hero-navy text-white p-10 md:p-16 text-center">
            <h2 className="text-3xl md:text-5xl font-bold">{t("partners.ctaTitle")}</h2>
            <p className="mt-4 text-white/70 max-w-xl mx-auto">{t("partners.ctaLead")}</p>
            <Link to="/contact" className="mt-8 inline-flex rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-primary">{t("partners.ctaBtn")}</Link>
          </div>
        </div>
      </section>
    </>
  );
}
